// The AeriosIQ palette.
//
// Dark is the Aerios Design System's own ramp, consumed from `aerios.gen.ts` —
// no value below is retyped, because a retyped hex is a fork of the design
// system that nobody labelled as one (D32).
//
// Light is D82, and it is DERIVED rather than invented. The system's light set
// is six tokens for one marketing band and says so outright; a product needs a
// full second mode. Every derived value keeps an Aerios hue and moves only
// lightness, and the SHAPE is taken from the dark ramp, which is already
// decided (D71). Light and dark therefore have the same number of steps doing
// the same jobs at very nearly the same measured ratios:
//
//   job          dark                    on ink-900   light                on canvas
//   headings     --white-50              15.42        --light-heading      18.37
//   body         --grey-300               8.04        DERIVED #454C4F       8.06
//   labels       --grey-500               5.65        --light-body          5.60
//   non-text     --grey-600 (fails AA)    3.94        DERIVED #707C81       3.96
//
// Two of the four light steps are the system's own values, used in the role
// their measured ratio actually fits: `--light-body` at 5.60 does the LABEL job
// that `--grey-500` does at 5.65, not the body job. Reading the system's six
// tokens as a four-step ramp is what makes the light theme fall out of D71
// rather than out of taste.
//
// Every ratio in the comments below was measured, not estimated. The
// `contrast.test.ts` beside this file re-measures them on every run, so a value
// that stops clearing its floor fails the build rather than shipping.
import { aerios } from "./aerios.gen.js";
// TRIAL (23 Sep 2026) — the handoff palette as an overlay. One file, one flag;
// see `trialPalette.ts` for what it takes from Daniel and what it refuses.
import { TRIAL_ACTIVE, trialPalette } from "./trialPalette.js";

const tl = TRIAL_ACTIVE ? trialPalette.light : null;
const td = TRIAL_ACTIVE ? trialPalette.dark : null;

// ── D82 · the values the design system does not have ───────────────────────
//
// This is the ONLY block in this file where a hex is written by hand, and every
// one carries the ratio it was derived to hit. They are handed back to the
// design-system project as D82 (D62 / 2D P4) — see PLATFORM-DECISIONS.md.
const d82 = {
  /** Body text. The job `--grey-300` does at 8.04 on dark. */
  textSecondary: "#454C4F", // 8.06 canvas · 8.75 raised · 7.48 sunken
  /**
   * NON-TEXT ONLY. Fails AA on every light surface BY DESIGN, exactly as
   * `--grey-600` does on dark, and clears the 3:1 bar that applies to non-text
   * UI. The token name does not warn you, so the rule has to.
   */
  borderStrong: "#707C81", // 3.96 canvas · 4.30 raised · 3.67 sunken
  /**
   * D14 permits 2.4:1 for a disabled control — the one place the floor drops,
   * because a disabled control has to READ as unavailable. Submit buttons are
   * never disabled: the user can always press, and is told what to fix.
   */
  textDisabled: "#98A2A6", // 2.40 canvas
  /**
   * Hover DARKENS on light and LIGHTENS on dark. An accent moving toward the
   * ground on interaction would be a defect in either mode.
   */
  accentHover: "#085E56", // 7.05 canvas
  accentActive: "#07514A", // 8.47 canvas
} as const;

// ── status · D81's five slots, in both modes ───────────────────────────────
//
// Named by MEANING so a product surface never invents a status colour at the
// call site. The dark values are the system's. The light values are the SAME
// FIVE HUES at light lightness — same slot, same meaning, measured against the
// light grounds. That is part of D82, not a new decision.
//
// Two hues are permanently barred from status (D81): `--aqua-*`, because the
// accent means INTERACTION (P3) and a green-blue "success" would make every
// success look clickable; and `--brand-violet`, reserved for the second chart
// series in product only (D20).
//
// Each light value is targeted at 5.0 on the canvas so a slot can also carry
// INLINE TEXT — which D81 permits wherever nothing is tinted behind it: a
// severity word in a table cell, an error line under a field.
const status = {
  dark: {
    neutral: aerios.statusNeutral, // 5.65 / 5.29 / 4.80
    info: aerios.statusInfo, // 8.90 / 8.34 / 7.57
    success: aerios.statusSuccess, // 8.67 / 8.12 / 7.37
    attention: aerios.statusAttention, // 8.35 / 7.82 / 7.10
    critical: aerios.statusCritical, // 5.57 / 5.22 / 4.74
  },
  light: {
    neutral: "#5F6C71", // 5.00 canvas · 5.43 raised · 4.64 sunken
    info: "#0B6CBA", // 5.00 / 5.43 / 4.64
    success: "#27784F", // 4.98 / 5.40 / 4.62
    attention: "#8D6213", // 4.97 / 5.40 / 4.62
    critical: "#CF1F13", // 5.01 / 5.44 / 4.65
  },
} as const;

// ── severity · D83 ─────────────────────────────────────────────────────────
//
// D81 left the severity grade scale open on purpose — "product-shaped, needs
// its own numbers, should come UP from the refactor". It does, here.
//
// The first attempt derived five fresh hues at a common contrast target and
// produced a severity critical sitting TWO units from `--status-critical`: two
// tokens, one colour, different meanings — the precise thing D81's slot
// discipline exists to prevent, arrived at by trying to avoid it.
//
// The mistake was treating severity as a second categorical palette. It is not.
// Red already MEANS blocking in this system, amber already MEANS needs a human,
// blue already MEANS informational. Re-deriving those hues at slightly
// different lightness distinguishes nothing; it duplicates a vocabulary and
// hopes nobody notices.
//
// So D83 REUSES the slots wherever the meaning genuinely coincides, and adds
// exactly ONE value — `high`, which has no status equivalent because no
// lifecycle state sits between "failed" and "needs a human".
//
// What keeps a severity mark and a status chip apart is then FORM, not hue,
// which is what D26 requires anyway: colour is never the only signal. Status
// renders as D81's tonal chip (fill + keyline + dot). Severity renders as a
// graded mark. Two shapes, one hue vocabulary, no invented values.
const severity = {
  dark: {
    critical: aerios.statusCritical,
    high: "#E98C57", // D83, the one new value · 6.92 / 6.49 / 5.89
    medium: aerios.statusAttention,
    low: aerios.statusInfo,
    info: aerios.statusNeutral,
  },
  light: {
    critical: status.light.critical,
    high: "#AE4D17", // 4.99 canvas · 5.42 raised · 4.63 sunken
    medium: status.light.attention,
    low: status.light.info,
    info: status.light.neutral,
  },
} as const;

// ── series · D84 ───────────────────────────────────────────────────────────
//
// The other ramp D81 left open. This app has no chart library — the "charts"
// are three hand-rolled stacked bars, a canvas histogram, six FleetMap encoding
// modes and the bounding-box defaults — but they all need an ordered set of
// marks that stay apart from each other and from the ground.
//
// Series 1 is the accent. Series 2 is `--brand-violet`, which D20 already
// permits as the second series IN PRODUCT ONLY.
//
// FINDING FOR THE SYSTEM: `--brand-violet #784AF5` measures 3.40 / 3.18 / 2.89
// on ink-900/800/700 — it FAILS the 3:1 non-text bar on `--ink-700`, the
// surface a chart most often sits on. D20 permits the hue but the shipped value
// cannot carry it on a raised surface, so series 2 on dark is a lifted variant.
// Recorded in PLATFORM-DECISIONS.md for the system to settle.
const series = {
  dark: [
    aerios.aqua400, // 9.91 / 9.29 / 8.43   the accent
    "#8961F6", // 4.23 / 3.96 / 3.60   --brand-violet, LIFTED (see above)
    aerios.statusInfo, // 8.90 / 8.34 / 7.57
    aerios.statusAttention, // 8.35 / 7.82 / 7.10
    aerios.statusSuccess, // 8.67 / 8.12 / 7.37
    aerios.statusCritical, // 5.57 / 5.22 / 4.74
  ],
  // Darkened to 4.5 on the canvas so a mark reads as a mark rather than a tint.
  // The light set is targeted higher than the 3:1 non-text bar deliberately:
  // these are small marks on a bright ground, where the bar is not the problem.
  light: [
    "#197F73", // 4.47 canvas
    "#7C4FF5", // 4.51
    "#0C73C7", // 4.51
    "#976814", // 4.49
    "#2A8055", // 4.48
    "#DD2114", // 4.50
  ],
} as const;

// ── provider · D92 ─────────────────────────────────────────────────────────
//
// Who provides a workflow node (the Workflows console's state board, ENT-UI-05):
// a TENANT node (other tenants may work on it through an access role) or a
// PLATFORM node. The colour marks the card's header tint, its 1px edge and its
// price tag, and the legend dot. It NEVER carries text — the card's words are
// the theme's text colours on top of the tint.
//
// Why these and not something already here:
//   - not aqua (D78: the accent means interaction, and every card is clickable
//     already — an aqua card would read as "the selected one");
//   - not a D81 status hue (red / amber / blue / green / neutral grey ALREADY
//     mean conflict / not granted / note / … on the same card);
//   - not `--brand-violet #784AF5` and not series 2 (D20 reserves that hue for
//     the second chart series): the tenant purple is turned ~15° toward red
//     (hue 269–273° vs 256°) so the two never read as the same colour.
//
// `platform` is the NON-TEXT step of each mode's neutral ramp — the same job
// D82's table gives it (dark `--grey-600` 3.94, light `#707C81` 3.96) — and so
// is quieter than the status neutral (`--grey-500`), which carries text.
//
// `client` (D93, M5-NODE-00) is our code in the pilot's browser changing files
// on the pilot's PC — "Pilot PC" on the card. A ROSE at hue ~330°: not aqua,
// not the violet, not a status hue; >= 20° from the tenant purple and >= 25°
// from the status red (contrast.test.ts asserts both).
//
// Measured (contrast.test.ts re-measures every one), dot on default / raised /
// sunken:
//   tenant   dark #BE8EF0 6.89 / 6.46 / 5.86   light #8A3FC7 5.35 / 5.81 / 4.96
//   platform dark #6E7A80 3.94 / 3.69 / 3.35   light #707C81 3.96 / 4.30 / 3.67
//   client   dark #E283B1 7.32 / 6.21 / 6.78   light #AD2F6F 6.15 / 6.15 / 5.67
// The card title (text.primary) on the 20% header tint over raised: tenant
// 10.11 dark / 14.88 light, platform 11.43 / 15.75, client 10.15 / 14.52.
const provider = {
  dark: {
    tenant: "#BE8EF0",
    platform: aerios.grey600,
    client: "#E283B1",
  },
  light: {
    tenant: "#8A3FC7",
    platform: d82.borderStrong,
    client: "#AD2F6F",
  },
} as const satisfies Record<
  "light" | "dark",
  { tenant: string; platform: string; client: string }
>;

/** D92 — how strongly the provider colour shows on a card (the walkthrough's recipe). */
const providerMix = {
  /** The card's 1px edge, its header's bottom edge and the price tag's edge. */
  edgeAlpha: 0.55,
  /** The header fill: the provider colour mixed into the card's surface. */
  headerAlpha: 0.2,
} as const;

// ── the chip recipe · D81 ──────────────────────────────────────────────────
//
// A status colour may carry the MEANING or carry the CONTRAST — never both at
// once. On a tonal chip the hue owns the fill, the dot and the keyline; THE
// LABEL IS NEUTRAL.
//
// This is not a preference. A tonal fill raises the ground TOWARD the colour
// sitting on it, so both ends of the contrast pair move together and the
// measurement collapses — worst for the darkest, most saturated hues. Measured
// on its own 18% chip, `--status-critical` reads 3.70:1 on ink-700 and
// `--status-neutral` 3.64:1. There is no single alpha that works, which is what
// proves the RECIPE rather than the constant was wrong.
//
// What changes between modes is only WHICH neutral. On dark it is `--white-50`
// (never below 8.95:1 across all fifteen slot/ground combinations). On a light
// ground an 18% tint stays light, so `--white-50` on those same chips measures
// 1.07:1 — the identical failure arriving from the other direction — and the
// label is the mode's own `text.primary`, worst case 15.24:1 across all five.
//
// The alphas do not move.
const chip = {
  fillAlpha: Number(aerios.statusFillAlpha), // 0.18
  keylineAlpha: Number(aerios.statusKeylineAlpha), // 0.55
} as const;

export const color = {
  light: {
    // D78 — the accent across every Aerios surface is aqua. On LIGHT surfaces
    // that is `--aqua-700`: `--aqua-400` measures 1.62:1 on the canvas and D78
    // bars it from light surfaces outright.
    primary: aerios.aqua700, // 5.56 canvas · 6.04 raised
    accent: d82.accentHover,
    accentActive: d82.accentActive,
    neutralDeep: aerios.ink900,

    severity: severity.light,
    status: status.light,
    series: series.light,
    provider: provider.light,
    providerMix,
    chip,

    surface: {
      default: tl?.surfaceDefault ?? aerios.light50, // the page
      paper: tl?.surfaceRaised ?? "#FFFFFF", // the TOP of the ladder, never the page
      subtle: tl?.surfaceBar ?? aerios.lightPanel,
      muted: tl?.surfaceBar ?? aerios.lightPanel,
      raised: tl?.surfaceRaised ?? "#FFFFFF",
      sunken: tl?.surfaceSunken ?? aerios.lightPanel,
    },

    text: {
      primary: tl?.textPrimary ?? aerios.lightHeading, // 18.37 canvas
      secondary: tl?.textSecondary ?? d82.textSecondary, // 8.06
      tertiary: tl?.textTertiary ?? aerios.lightBody, // 5.60
      disabled: d82.textDisabled, // 2.40 — D14
      inverse: aerios.white50,
    },

    border: tl?.border ?? aerios.lightBorder,
    borders: {
      subtle: tl?.border ?? aerios.lightBorder,
      strong: tl?.border ?? aerios.lightBorder,
      /**
       * The edge that IDENTIFIES a control — an input, a select, a checkbox.
       * WCAG 1.4.11 requires 3:1 for "visual information required to identify
       * user interface components", and this is the only border that carries
       * that duty. Hairlines above are structure (P2) and are deliberately
       * quieter than 3:1; treating them as the same thing is what this
       * separation fixes.
       */
      control: d82.borderStrong, // 3.96 canvas — NON-TEXT ONLY
    },

    /** The label on a tonal status chip. Never a status token — see `chip`. */
    chipLabel: aerios.lightHeading,
  },
  dark: {
    primary: aerios.aqua400, // 9.91 on ink-900
    accent: aerios.aqua300, // link hover only · 11.99
    accentActive: aerios.aqua500,
    neutralDeep: aerios.ink900,

    severity: severity.dark,
    status: status.dark,
    series: series.dark,
    provider: provider.dark,
    providerMix,
    chip,

    surface: {
      default: td?.surfaceDefault ?? aerios.ink900,
      paper: td?.surfaceRaised ?? aerios.ink800,
      subtle: td?.surfaceBar ?? aerios.ink800,
      muted: td?.surfaceBar ?? aerios.ink800,
      raised: td?.surfaceRaised ?? aerios.ink800,
      sunken: td?.surfaceSunken ?? aerios.ink700,
    },

    text: {
      primary: td?.textPrimary ?? aerios.white50, // 15.42
      secondary: td?.textSecondary ?? aerios.grey300, // 8.04
      tertiary: td?.textTertiary ?? aerios.grey500, // 5.65
      /**
       * D14's 2.4:1 disabled allowance. `--grey-600` is the system's own
       * non-text step at 3.94 and is the closest sanctioned value; going dimmer
       * would need a token the system does not have.
       */
      disabled: aerios.grey600,
      inverse: aerios.ink900,
    },

    border: td?.border ?? aerios.line600, // the primary structural device (P2)
    borders: {
      subtle: td?.border ?? aerios.line600,
      strong: td?.border ?? aerios.line500, // hairline, emphasised — table head
      /**
       * See the light counterpart. `--grey-600` is the system's designated
       * non-text token and its own comment says why: 3.94:1 on `--ink-900`,
       * below the AA text floor on every surface BY DESIGN, clearing the 3:1
       * bar that applies to non-text UI. `--line-500` measures 1.66:1 and is a
       * hairline — using it as a control edge was the conflation the contrast
       * test caught.
       */
      control: aerios.grey600, // 3.94 / 3.69 / 3.35 — NON-TEXT ONLY
    },

    chipLabel: aerios.white50, // = --status-label
  },
} as const;

export type SeverityKey = keyof typeof severity.light;
export type StatusKey = keyof typeof status.light;
/** @deprecated The soft-pill buckets are gone; use `StatusKey` (D81's slots). */
export type StatusPillKey = StatusKey;

export type ProviderKey = keyof typeof provider.light;

export { status, severity, series, chip, provider, providerMix };

/**
 * Sidebar chrome. Hierarchy by ALPHA on the text colour, not by hue — the
 * original brief's reasoning holds unchanged under Aerios, and P3 sharpens it:
 * a tinted accent background is legitimate only where it signals selection or
 * current position, never as ornament.
 *
 * The selected row is the one place that test is met, so it keeps the accent —
 * now aqua rather than Signal Blue. On dark that fill is `--aqua-400`, which
 * means the label on it must be `--ink-900` and not white: D78's consequence
 * that "every primary button inverts its label colour rather than merely
 * changing hue" applies to every filled accent surface, and this is one.
 */
export const sidebar = {
  light: {
    rowHoverBg: "rgba(7, 9, 11, 0.04)",
    rowPressedBg: "rgba(7, 9, 11, 0.08)",
    selectedBg: aerios.aqua700, // 5.56 on the canvas
    selectedFg: aerios.white50, // 5.35 on the fill
    tooltipBg: "rgba(7, 9, 11, 0.92)",
    tooltipFg: aerios.white50,
  },
  dark: {
    rowHoverBg: "rgba(238, 242, 241, 0.06)",
    rowPressedBg: "rgba(238, 242, 241, 0.10)",
    selectedBg: aerios.aqua400,
    selectedFg: aerios.ink900, // 9.91 — white here measures 1.76 and fails
    tooltipBg: "rgba(38, 40, 42, 0.96)",
    tooltipFg: aerios.white50,
  },
} as const;

export type SidebarTokens = (typeof sidebar)["light"];

/**
 * The Platform workspace marker — AND AN OPEN QUESTION, recorded rather than
 * answered.
 *
 * This used to be `systemIndigo #5856D6`: a SECOND accent, whose job was to
 * make "you are operating across tenants" unmistakable. Under the previous
 * guide that was sourced from a sanctioned HIG table and was legitimate.
 *
 * Under Aerios it is not. P3 makes aqua the only colour permitted to signal
 * interaction, live state, data or current position, and decorative accent a
 * defect. `--brand-violet` cannot take over either: D20 reserves it for the
 * second chart series in product, and D81 bars it from status permanently.
 * Differentiating a workspace by hue is a decision the design system has not
 * made, and inventing a second Aerios accent to preserve the effect would be
 * exactly the drift this refactor exists to stop.
 *
 * So the MECHANISM is kept and pointed at a neutral treatment: the Platform
 * workspace is marked, but by weight rather than by a hue nobody decided on.
 * The question goes back to the system — see PLATFORM-DECISIONS.md.
 *
 * If the answer is "a second hue is warranted", this is the one place that has
 * to change.
 */
export const workspaceAccent = {
  light: {
    solid: aerios.lightHeading,
    solidFg: aerios.light50,
    fg: d82.textSecondary,
    tint: "rgba(7, 9, 11, 0.06)",
  },
  dark: {
    solid: aerios.white50,
    solidFg: aerios.ink900,
    fg: aerios.grey300,
    tint: "rgba(238, 242, 241, 0.10)",
  },
} as const;

export type WorkspaceAccentTokens = (typeof workspaceAccent)["light"];
